// src/components/layout/AdminLayout.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import './AdminLayout.css';

const AdminLayout = () => {
  console.log("AdminLayout rendering"); // Debug log
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Activity tracking
  const { currentUser, logout } = useAuth();
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [remainingTime, setRemainingTime] = useState(600); // 600 seconds = 10 minutes
  const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  
  // Handle mobile/desktop responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Start countdown interval - runs continuously
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    console.log("Starting countdown interval");
    
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      const remaining = Math.max(0, Math.ceil((INACTIVITY_TIMEOUT - timeSinceLastActivity) / 1000));
      
      setRemainingTime(remaining);
      
      // Debug log
      console.log("Countdown update:", {
        timeSinceLastActivity: Math.floor(timeSinceLastActivity / 1000),
        remaining: remaining
      });
      
    }, 1000);

    return () => {
      console.log("Clearing countdown interval");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentUser, INACTIVITY_TIMEOUT]);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    
    console.log("Activity detected, resetting timer");
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set remaining time to full timeout
    setRemainingTime(600);
    
    // Set new timeout for logout
    timeoutRef.current = setTimeout(() => {
      console.log("User inactive for 10 minutes, logging out...");
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout, INACTIVITY_TIMEOUT]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    if (currentUser) {
      resetActivityTimer();
    }
  }, [currentUser, resetActivityTimer]);

  // Set up activity tracking
  useEffect(() => {
    if (!currentUser) {
      return; // Don't track activity if user is not authenticated
    }

    // List of events that indicate user activity
    const activityEvents = [
      'click',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
      'mousedown',
      'keypress'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Initialize timer on component mount
    resetActivityTimer();

    console.log("Activity tracking started - 10 minute timeout");

    // Cleanup function
    return () => {
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      console.log("Activity tracking stopped");
    };
  }, [currentUser, handleUserActivity, resetActivityTimer]);
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };
  
  return (
    <div className="admin-layout">
      {isMobile && !sidebarCollapsed && (
        <div className="mobile-overlay" onClick={closeSidebar}></div>
      )}
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        toggleSidebar={toggleSidebar} 
        isMobile={isMobile}
        remainingTime={remainingTime}
      />
      <main className={`content ${sidebarCollapsed ? 'expanded' : ''}`}>
        {isMobile && (
          <div className="mobile-header">
            <button 
              className="mobile-menu-button"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              <div className={`hamburger-icon ${!sidebarCollapsed ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        )}
        <div className="main-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;